import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { IAPIResponse, IHTTPResponse } from "../common/models/httpModels";
import { toast } from "react-toastify";
import FacilitatorService from "./facilitatorService";
import { Facilitator } from "./facilitator";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from '@mui/icons-material/Add';
import { GridApi } from "ag-grid-community";
import moment from 'moment';
import { useNavigate } from "react-router-dom";
import { ISchoolModel } from "./models/ISchoolModel";
import { IFoundationProgram } from "../common/models/foundationProgram";
import { IOlympiadExam } from "../common/models/olympiadExam";
import CustomLoadingCellRenderer from "../common/components/CustomLoadingCellRenderer";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FacilitatorSchoolService from "./facilitator-school/facilitatorSchoolService";
import { IDeleteSchoolUserRequest } from "./facilitator-school/facilitatorSchoolModel";
import FolderIcon from '@mui/icons-material/Folder';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';

// ── Quick-view panel ──────────────────────────────────────────────────────────

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value?: string | null }> = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 0.5 }}>
      <Box sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</Typography>
        <Typography variant="body2">{value}</Typography>
      </Box>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {title}
    </Typography>
    <Divider sx={{ mt: 0.5, mb: 1 }} />
    {children}
  </Box>
);

const ProgramChips: React.FC<{ items: string[]; color: string }> = ({ items, color }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
    {items.map(i => <Chip key={i} label={i} size="small" sx={{ bgcolor: color, fontWeight: 500, fontSize: 11 }} />)}
  </Box>
);

const SchoolQuickView: React.FC<{
  school: ISchoolModel | null;
  onClose: () => void;
  onEdit: (s: ISchoolModel) => void;
  onFiles: (s: ISchoolModel) => void;
  onDelete: (s: ISchoolModel) => void;
}> = ({ school, onClose, onEdit, onFiles, onDelete }) => {
  const fps = school?.foundationPrograms?.map(f => f.programName) ?? [];
  const oes = school?.olympiadExams?.map(e => e.examName) ?? [];
  const vls = (school as any)?.videoLessionPrograms?.map((v: any) => v.programName ?? v.name ?? v) ?? [];

  return (
    <Drawer
      anchor="right"
      open={!!school}
      onClose={onClose}
      PaperProps={{ sx: { width: 360, p: 0 } }}
    >
      {school && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ bgcolor: 'primary.main', px: 2.5, pt: 2.5, pb: 2, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 0.75, display: 'flex' }}>
                  <SchoolIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>{school.schoolName}</Typography>
                  {school.state && (
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>{school.state}</Typography>
                  )}
                </Box>
              </Box>
              <IconButton size="small" onClick={onClose} sx={{ color: 'white', mt: -0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />} onClick={() => onEdit(school)}>
              Edit
            </Button>
            <Button size="small" variant="outlined" startIcon={<FolderIcon fontSize="small" />} onClick={() => onFiles(school)}>
              Files
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlinedIcon fontSize="small" />} onClick={() => onDelete(school)}>
              Delete
            </Button>
          </Box>

          {/* Body */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
            <Section title="School Info">
              <InfoRow icon={<PersonIcon sx={{ fontSize: 16 }} />} label="Username" value={school.userName} />
              <InfoRow icon={<EmailIcon sx={{ fontSize: 16 }} />} label="Email" value={school.email} />
              <InfoRow icon={<PhoneIcon sx={{ fontSize: 16 }} />} label="Mobile" value={school.mobileNumber} />
              <InfoRow icon={<LocationOnIcon sx={{ fontSize: 16 }} />} label="Address" value={school.address} />
            </Section>

            {(fps.length > 0) && (
              <Section title="Foundation Programmes">
                <ProgramChips items={fps} color="#e3f2fd" />
              </Section>
            )}

            {(oes.length > 0) && (
              <Section title="Olympiad Exams">
                <ProgramChips items={oes} color="#e8f5e9" />
              </Section>
            )}

            {(vls.length > 0) && (
              <Section title="Video Lesson Access">
                <ProgramChips items={vls} color="#fff3e0" />
              </Section>
            )}

            {school.abhyasaBharathiMagazineAccess && (
              <Section title="Magazine">
                <Chip label="Abhyasa Bharathi" size="small" icon={<AutoStoriesIcon sx={{ fontSize: 14 }} />} sx={{ bgcolor: '#f3e5f5', fontWeight: 500 }} />
              </Section>
            )}

            {(school.chairmanName || school.principalName || school.programInChargeName) && (
              <Section title="Contacts">
                {school.chairmanName && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Chairman</Typography>
                    <Typography variant="body2">{school.chairmanName}</Typography>
                    {school.chairmanWhatsAppContact && (
                      <Typography variant="caption" color="text.secondary">{school.chairmanWhatsAppContact}</Typography>
                    )}
                  </Box>
                )}
                {school.principalName && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Principal</Typography>
                    <Typography variant="body2">{school.principalName}</Typography>
                    {school.principalWhatsAppContact && (
                      <Typography variant="caption" color="text.secondary">{school.principalWhatsAppContact}</Typography>
                    )}
                  </Box>
                )}
                {school.programInChargeName && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Program In-charge</Typography>
                    <Typography variant="body2">{school.programInChargeName}</Typography>
                    {school.programInchargeWhatsAppContact && (
                      <Typography variant="caption" color="text.secondary">{school.programInchargeWhatsAppContact}</Typography>
                    )}
                  </Box>
                )}
              </Section>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const FacilitatorInstitutionsList: FunctionComponent = (props) => {
  const [gridApi, setGridApi] = useState<GridApi>();
  const [loading, setLoading] = useState(false);
  const [schoolList, setSchoolList] = useState<
    ISchoolModel[]
  >([]);
  const navigate = useNavigate();
  const loadSchools = () => {
    setLoading(true);
    FacilitatorService.getSchoolsList()
      .then((res: IHTTPResponse<IAPIResponse<ISchoolModel>>) => {
        if (res?.data?.errors?.length > 0) {
          res?.data?.errors.forEach((i) => toast.error(`${i.field}: ${i.message}`));
        }
        if (res.data !== null && res.data !== undefined) {
          setSchoolList(res.data.list);
        }
      })
      .catch(() => {
        toast.error("Error in Fetching Student Count");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadSchools();
  }, []);

  const getFProgramsText = (data: IFoundationProgram[]) => {
    if (!data) return '';
    return data.map(i => i.programName).join(', ');
  }
  const getOProgramsText = (data: IOlympiadExam[]) => {
    if (!data) return '';
    return data.map(i => i.examName).join(', ');
  }
  const handleEditClick = (e: any, data: ISchoolModel) => {
    navigate('/Schools/Edit/' + data.userId);
  }

  const handleFilesClick = (e: any, data: ISchoolModel) => {
    navigate('/Facilitator/sharedfiles/' + data.userId);
  }

  const handleDeleteClick = (e: any, data: ISchoolModel) => {
    if (!window.confirm(`Are you sure you want to delete "${data.schoolName}"?`)) return;
    FacilitatorSchoolService.DeleteMultiple([{ userId: data.userId } as IDeleteSchoolUserRequest]).then((res: IHTTPResponse<IAPIResponse<any>>) => {

      if (res?.data?.errors?.length > 0) {
        res?.data?.errors.forEach((i) => toast.error(`${i.field}: ${i.message}`));
      }
      if (res.data.data === true) {
        toast.success(
          'School deleted successfully'
        );
        loadSchools();
      }
      else {
        toast.error(
          'Error in Deleting the school'
        );
      }
    }).catch(() => {
      toast.error(
        'Error in Deleting the school'
      );
    });
  }

  // Each Column Definition results in one Column.
  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: "",
      field: "boolean",
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      resizable: false,
      filter: false,
      width: 44,
      flex: 0,
    },
    { headerName: "School Name", field: "schoolName", filter: true, minWidth: 180 },
    { headerName: "Address", field: "address", filter: true, minWidth: 160 },
    { headerName: "Username", field: "userName", filter: true, minWidth: 130 },
    {
      headerName: "Foundation Programs", field: "foundationPrograms", filter: true, minWidth: 200,
      cellRenderer: (data: any) => { return getFProgramsText(data.value); }
    },
    {
      headerName: "Olympiad Exams", field: "olympiadExams", filter: true, minWidth: 180,
      cellRenderer: (data: any) => { return getOProgramsText(data.value); }
    },
    {
      headerName: "Created", field: "createdOn", filter: true, minWidth: 140,
      cellRenderer: (data: any) => { return moment(data.value).format('D MMM YYYY'); }
    },
    {
      headerName: "Actions",
      field: "Actions",
      filter: false,
      sortable: false,
      width: 130,
      flex: 0,
      cellRenderer: (data: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title="Edit School">
            <IconButton size="small" color="primary" onClick={(e) => handleEditClick(e, data.data)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Shared Files">
            <IconButton size="small" onClick={(e) => handleFilesClick(e, data.data)}>
              <FolderIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete School">
            <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(e, data.data)}>
              <DeleteOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]);

  // // Example of consuming Grid Event
  // const cellClickedListener = useCallback((event: any) => {
  //   console.log("cellClicked", event);
  // }, []);
  // const handleRowSelected = useCallback((event: any) => {
  //   debugger;
  //   console.log(event.api.getSelectedRows());
  //   //FacilitatorSchoolService.DeleteMultiple
  // }, []);
  const handleGridReady = (gridParams: any) => {
    setGridApi(gridParams.api);
    gridParams.api.sizeColumnsToFit();
  };
  const getValue = (inputSelector: any) => {
    var text = document.querySelector(inputSelector).value;
    switch (text) {
      case 'none':
        return;
      case 'tab':
        return '\t';
      default:
        return text;
    }
  };
  const getParams = () => {
    return {
      fileName: 'Schools.csv',
      allColumns: true,
      processCellCallback: function (cellParams: any) {
        if (cellParams && cellParams.column.colId === 'createdOn') {
          return moment(cellParams.value).format('d MMM yyyy HH:mm');
        } else
          return cellParams.value // no formatting
      }
    };
  };
  const handleDownloadClick = () => {
    gridApi?.exportDataAsCsv(getParams());
  };
  const handleMultipleDeleteClick = () => {
    const selectedRows = gridApi?.getSelectedRows();
    if (selectedRows && selectedRows.length > 0) {
      if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} school(s)?`)) return;
      const request: IDeleteSchoolUserRequest[] = [];
      selectedRows.forEach((row: ISchoolModel) => { return request.push({ userId: row.userId } as IDeleteSchoolUserRequest); });
      FacilitatorSchoolService.DeleteMultiple(request).then((res: IHTTPResponse<IAPIResponse<any>>) => {

        if (res?.data?.errors?.length > 0) {
          res?.data?.errors.forEach((i) => toast.error(`${i.field}: ${i.message}`));
        }
        if (res.data.data === true) {
          toast.success(
            'Schools deleted successfully'
          );
          loadSchools();
        }
        else {
          toast.error(
            'Error in Deleting the schools'
          );
        }
      }).catch(() => {
        toast.error(
          'Error in Deleting the schools'
        );
      });
    }
  }
  const handleAddSchoolClick = () => {

    navigate('/Schools/Add');
  }
  const loadingCellRenderer = useCallback(CustomLoadingCellRenderer, []);
  const loadingCellRendererParams = useMemo(() => {
    return {
      loadingMessage: "One moment please...",
    };
  }, []);

  return (
    <Facilitator>
      <Grid container>
        <Grid item xs={12}>
          <Card sx={{ height: "100%" }}>
            {/* Page header */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Schools</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Manage school accounts and program access
                </Typography>
              </Box>
              <Chip label={`${schoolList.length} schools`} variant="outlined" size="small" />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Tooltip title="Export all schools as CSV">
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadClick}>
                  Export
                </Button>
              </Tooltip>
              <Tooltip title="Delete selected schools">
                <Button variant="outlined" color="error" size="small" startIcon={<DeleteOutlinedIcon />} onClick={handleMultipleDeleteClick}>
                  Delete Selected
                </Button>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddSchoolClick} sx={{ fontWeight: 600, px: 2 }}>
                Add School
              </Button>
            </Box>
            <Divider />
            {loading && <LinearProgress />}
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <div
                className="ag-theme-alpine"
                style={{ height: "75vh", width: "100%" }}
              >
                <AgGridReact
                  loadingCellRenderer={loadingCellRenderer}
                  loadingCellRendererParams={loadingCellRendererParams}
                  onGridReady={handleGridReady}
                  gridOptions={{
                    pagination: true,
                    paginationPageSize: 15,
                    defaultColDef: {
                      filter: true,
                      flex: 1,
                      floatingFilter: false,
                      resizable: true,
                      sortable: true,
                    },
                    rowHeight: 48,
                    headerHeight: 44,
                  }}
                  rowData={schoolList}
                  columnDefs={columnDefs}
                  animateRows={true}
                  rowSelection="multiple"
                />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Facilitator>
  );
};
